import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../lib/api';
import { Wand2, Plus, Trash2, ChevronDown, ChevronUp, GripVertical, Play, Save, Upload, X, Copy, Check, FolderOpen, ArrowRight } from 'lucide-react';

const ACCENT = '#f97316';

// ── Types ───────────────────────────────────────────────────────────

type TransformOperation =
  | 'map_fields' | 'filter_rows' | 'sort' | 'deduplicate'
  | 'merge' | 'split_field' | 'concat_fields' | 'regex_extract'
  | 'date_format' | 'number_format' | 'lookup_replace' | 'default_value'
  | 'uppercase' | 'lowercase' | 'trim' | 'json_parse' | 'json_path'
  | 'aggregate' | 'group_by' | 'calculate';

interface TransformStep {
  id: string;
  operation: TransformOperation;
  label: string;
  config: Record<string, unknown>;
  order: number;
}

interface SavedTransform {
  id: string;
  name: string;
  description: string;
  steps: TransformStep[];
  usage_count: number;
  created_at: string;
  updated_at: string;
}

// ── Operation Metadata ──────────────────────────────────────────────

const OPERATIONS: { value: TransformOperation; label: string; category: string; description: string }[] = [
  { value: 'map_fields', label: 'Map Fields', category: 'Structure', description: 'Rename or remap field names' },
  { value: 'filter_rows', label: 'Filter Rows', category: 'Structure', description: 'Keep rows matching a condition' },
  { value: 'sort', label: 'Sort', category: 'Structure', description: 'Sort rows by a field' },
  { value: 'deduplicate', label: 'Deduplicate', category: 'Structure', description: 'Remove duplicate rows by key' },
  { value: 'merge', label: 'Merge', category: 'Structure', description: 'Merge with external data by key' },
  { value: 'concat_fields', label: 'Concat Fields', category: 'Combine', description: 'Combine multiple fields into one' },
  { value: 'split_field', label: 'Split Field', category: 'Combine', description: 'Split a field by separator' },
  { value: 'regex_extract', label: 'Regex Extract', category: 'Extract', description: 'Extract pattern from field' },
  { value: 'json_parse', label: 'JSON Parse', category: 'Extract', description: 'Parse JSON string field' },
  { value: 'json_path', label: 'JSON Path', category: 'Extract', description: 'Extract value from JSON field' },
  { value: 'date_format', label: 'Date Format', category: 'Format', description: 'Reformat date strings' },
  { value: 'number_format', label: 'Number Format', category: 'Format', description: 'Round, format currency' },
  { value: 'uppercase', label: 'Uppercase', category: 'Text', description: 'Convert to uppercase' },
  { value: 'lowercase', label: 'Lowercase', category: 'Text', description: 'Convert to lowercase' },
  { value: 'trim', label: 'Trim', category: 'Text', description: 'Remove whitespace' },
  { value: 'default_value', label: 'Default Value', category: 'Clean', description: 'Set default for empty fields' },
  { value: 'lookup_replace', label: 'Lookup Replace', category: 'Clean', description: 'Replace values from lookup table' },
  { value: 'aggregate', label: 'Aggregate', category: 'Math', description: 'Sum, count, avg, min, max' },
  { value: 'group_by', label: 'Group By', category: 'Math', description: 'Group and aggregate' },
  { value: 'calculate', label: 'Calculate', category: 'Math', description: 'Math expressions ({field1} + {field2})' },
];

const CATEGORY_COLORS: Record<string, string> = {
  Structure: '#3b82f6',
  Combine: '#8b5cf6',
  Extract: '#ec4899',
  Format: '#f97316',
  Text: '#10b981',
  Clean: '#6366f1',
  Math: '#ef4444',
};

const FILTER_OPERATORS = ['=', '!=', '>', '<', '>=', '<=', 'contains', 'not_contains', 'starts_with', 'ends_with', 'is_empty', 'is_not_empty'];
const DATE_FORMATS = ['iso', 'date', 'us', 'eu', 'epoch', 'epoch_s', 'time'];
const NUMBER_FORMATS = ['round', 'floor', 'ceil', 'currency', 'percent', 'integer'];
const AGGREGATE_OPS = ['sum', 'avg', 'min', 'max', 'count'];

// ── Helpers ─────────────────────────────────────────────────────────

function uuid() {
  return crypto.randomUUID();
}

function parseInputData(text: string): Record<string, unknown>[] {
  const trimmed = text.trim();
  if (!trimmed) return [];

  // Try JSON first
  try {
    const parsed = JSON.parse(trimmed);
    if (Array.isArray(parsed)) return parsed;
    if (typeof parsed === 'object') return [parsed];
  } catch { /* not JSON */ }

  // Try CSV
  const lines = trimmed.split('\n').map(l => l.trim()).filter(Boolean);
  if (lines.length < 2) return [];

  const detectSep = (line: string) => {
    if (line.includes('\t')) return '\t';
    if (line.includes(',')) return ',';
    if (line.includes(';')) return ';';
    if (line.includes('|')) return '|';
    return ',';
  };

  const sep = detectSep(lines[0]);
  const headers = lines[0].split(sep).map(h => h.trim().replace(/^["']|["']$/g, ''));

  return lines.slice(1).map(line => {
    const values = line.split(sep).map(v => v.trim().replace(/^["']|["']$/g, ''));
    const row: Record<string, unknown> = {};
    headers.forEach((h, i) => { row[h] = values[i] ?? ''; });
    return row;
  });
}

function getFieldNames(data: Record<string, unknown>[]): string[] {
  const fields = new Set<string>();
  for (const row of data.slice(0, 50)) {
    for (const key of Object.keys(row)) fields.add(key);
  }
  return Array.from(fields);
}

// ── Main Page ───────────────────────────────────────────────────────

export default function TransformStudioPage() {
  const [inputText, setInputText] = useState('');
  const [inputData, setInputData] = useState<Record<string, unknown>[]>([]);
  const [steps, setSteps] = useState<TransformStep[]>([]);
  const [outputData, setOutputData] = useState<Record<string, unknown>[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [expandedStep, setExpandedStep] = useState<string | null>(null);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  // Save/load
  const [savedTransforms, setSavedTransforms] = useState<SavedTransform[]>([]);
  const [saveName, setSaveName] = useState('');
  const [saveDesc, setSaveDesc] = useState('');
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  // Drag state
  const dragItem = useRef<number | null>(null);
  const dragOverItem = useRef<number | null>(null);

  const fields = getFieldNames(inputData);

  // Parse input
  const handleInputChange = useCallback((text: string) => {
    setInputText(text);
    const parsed = parseInputData(text);
    setInputData(parsed);
  }, []);

  // Live preview via API
  const runPreview = useCallback(async () => {
    if (inputData.length === 0 || steps.length === 0) {
      setOutputData([]);
      setErrors([]);
      return;
    }
    setPreviewing(true);
    try {
      const res = await api.post<{ result: Record<string, unknown>[]; errors: string[] }>('/transforms/preview', {
        data: inputData.slice(0, 1000),
        steps,
      });
      setOutputData(res.result);
      setErrors(res.errors);
    } catch (err: any) {
      setErrors([err.message || 'Preview failed']);
    }
    setPreviewing(false);
  }, [inputData, steps]);

  // Auto-preview on changes
  useEffect(() => {
    if (inputData.length === 0 || steps.length === 0) {
      setOutputData([]);
      setErrors([]);
      return;
    }
    const timer = setTimeout(runPreview, 400);
    return () => clearTimeout(timer);
  }, [inputData, steps, runPreview]);

  // Load saved transforms
  const loadSaved = useCallback(async () => {
    try {
      const res = await api.get<{ transforms: SavedTransform[] }>('/transforms');
      setSavedTransforms(res.transforms);
    } catch { /* empty */ }
  }, []);

  useEffect(() => { loadSaved(); }, [loadSaved]);

  // Add step
  const addStep = (operation: TransformOperation) => {
    const opMeta = OPERATIONS.find(o => o.value === operation)!;
    const step: TransformStep = {
      id: uuid(),
      operation,
      label: opMeta.label,
      config: {},
      order: steps.length,
    };
    setSteps([...steps, step]);
    setExpandedStep(step.id);
    setShowAddMenu(false);
  };

  // Update step config
  const updateStep = (id: string, config: Record<string, unknown>) => {
    setSteps(prev => prev.map(s => s.id === id ? { ...s, config } : s));
  };

  // Remove step
  const removeStep = (id: string) => {
    setSteps(prev => prev.filter(s => s.id !== id).map((s, i) => ({ ...s, order: i })));
    if (expandedStep === id) setExpandedStep(null);
  };

  // Drag reorder
  const handleDragStart = (index: number) => { dragItem.current = index; };
  const handleDragEnter = (index: number) => { dragOverItem.current = index; };
  const handleDragEnd = () => {
    if (dragItem.current === null || dragOverItem.current === null) return;
    const newSteps = [...steps];
    const [removed] = newSteps.splice(dragItem.current, 1);
    newSteps.splice(dragOverItem.current, 0, removed);
    setSteps(newSteps.map((s, i) => ({ ...s, order: i })));
    dragItem.current = null;
    dragOverItem.current = null;
  };

  // Save
  const handleSave = async () => {
    if (!saveName.trim() || steps.length === 0) return;
    setSaving(true);
    try {
      await api.post('/transforms', {
        name: saveName.trim(),
        description: saveDesc.trim(),
        steps,
        input_schema: fields.map(f => ({ field: f, type: 'string' })),
        output_schema: getFieldNames(outputData).map(f => ({ field: f, type: 'string' })),
      });
      setShowSaveModal(false);
      setSaveName('');
      setSaveDesc('');
      loadSaved();
    } catch { /* empty */ }
    setSaving(false);
  };

  // Load saved transform
  const loadTransform = async (id: string) => {
    try {
      const res = await api.get<{ transform: SavedTransform }>(`/transforms/${id}`);
      setSteps(res.transform.steps);
      setShowLoadModal(false);
    } catch { /* empty */ }
  };

  // Delete saved transform
  const deleteSaved = async (id: string) => {
    try {
      await api.delete(`/transforms/${id}`);
      loadSaved();
    } catch { /* empty */ }
  };

  // Copy output JSON
  const copyOutput = () => {
    navigator.clipboard.writeText(JSON.stringify(outputData, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // File upload
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      handleInputChange(text);
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div style={{ width: 40, height: 40, borderRadius: '0.5rem', background: `linear-gradient(135deg, ${ACCENT}, #ea580c)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Wand2 size={20} color="#fff" />
          </div>
          <div>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0, color: '#111827' }}>Transform Studio</h1>
            <p style={{ fontSize: '0.8rem', color: '#6b7280', margin: 0 }}>Build data transformation pipelines with live preview</p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={() => setShowLoadModal(true)} style={btnSecondary}><FolderOpen size={16} /> Load</button>
          <button onClick={() => setShowSaveModal(true)} disabled={steps.length === 0} style={{ ...btnPrimary, opacity: steps.length === 0 ? 0.5 : 1 }}><Save size={16} /> Save</button>
        </div>
      </div>

      {/* 3-panel layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px 1fr', gap: '1rem', alignItems: 'start' }}>
        {/* Left: Input */}
        <div style={panelStyle}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
            <h2 style={panelTitle}>Input Data</h2>
            <label style={{ ...btnSmall, cursor: 'pointer' }}>
              <Upload size={14} /> Upload CSV
              <input type="file" accept=".csv,.json,.tsv,.txt" onChange={handleFileUpload} style={{ display: 'none' }} />
            </label>
          </div>
          <textarea
            value={inputText}
            onChange={e => handleInputChange(e.target.value)}
            placeholder={'Paste JSON array or CSV here...\n\nJSON: [{"name":"Alice","age":30},{"name":"Bob","age":25}]\n\nCSV:\nname,age,city\nAlice,30,NYC\nBob,25,LA'}
            style={{ width: '100%', height: 180, border: '1px solid #e5e7eb', borderRadius: '0.375rem', padding: '0.75rem', fontFamily: 'ui-monospace, monospace', fontSize: '0.8rem', resize: 'vertical', boxSizing: 'border-box' }}
          />
          {inputData.length > 0 && (
            <div style={{ marginTop: '0.75rem' }}>
              <p style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.5rem' }}>{inputData.length} rows, {fields.length} fields</p>
              <DataTable data={inputData} maxRows={10} />
            </div>
          )}
        </div>

        {/* Center: Pipeline */}
        <div style={panelStyle}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
            <h2 style={panelTitle}>Pipeline</h2>
            <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{steps.length} step{steps.length !== 1 ? 's' : ''}</span>
          </div>

          {steps.length === 0 && (
            <div style={{ textAlign: 'center', padding: '2rem 1rem', color: '#9ca3af', fontSize: '0.85rem' }}>
              <Wand2 size={32} style={{ margin: '0 auto 0.75rem', opacity: 0.3 }} />
              <p style={{ margin: 0 }}>No steps yet</p>
              <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem' }}>Click "Add Step" below to start</p>
            </div>
          )}

          {steps.map((step, index) => {
            const opMeta = OPERATIONS.find(o => o.value === step.operation);
            const catColor = CATEGORY_COLORS[opMeta?.category || ''] || '#6b7280';
            const isExpanded = expandedStep === step.id;

            return (
              <div
                key={step.id}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragEnter={() => handleDragEnter(index)}
                onDragEnd={handleDragEnd}
                onDragOver={e => e.preventDefault()}
                style={{ border: '1px solid #e5e7eb', borderRadius: '0.375rem', marginBottom: '0.5rem', backgroundColor: isExpanded ? '#f9fafb' : '#fff', transition: 'background-color 0.15s' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.75rem', cursor: 'pointer' }} onClick={() => setExpandedStep(isExpanded ? null : step.id)}>
                  <GripVertical size={14} style={{ cursor: 'grab', color: '#9ca3af', flexShrink: 0 }} />
                  <span style={{ fontSize: '0.7rem', fontWeight: 600, padding: '2px 6px', borderRadius: '4px', backgroundColor: catColor + '18', color: catColor, flexShrink: 0 }}>
                    {opMeta?.category}
                  </span>
                  <span style={{ fontSize: '0.8rem', fontWeight: 500, color: '#111827', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {step.label}
                  </span>
                  <button onClick={e => { e.stopPropagation(); removeStep(step.id); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: '#9ca3af' }}><Trash2 size={14} /></button>
                  {isExpanded ? <ChevronUp size={14} color="#9ca3af" /> : <ChevronDown size={14} color="#9ca3af" />}
                </div>

                {isExpanded && (
                  <div style={{ padding: '0 0.75rem 0.75rem', borderTop: '1px solid #e5e7eb' }}>
                    <StepConfig step={step} fields={fields} onChange={(config) => updateStep(step.id, config)} />
                  </div>
                )}

                {index < steps.length - 1 && (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: 0 }}>
                    <ArrowRight size={12} color="#d1d5db" style={{ transform: 'rotate(90deg)', margin: '-4px 0' }} />
                  </div>
                )}
              </div>
            );
          })}

          {/* Add Step */}
          <div style={{ position: 'relative', marginTop: '0.5rem' }}>
            <button onClick={() => setShowAddMenu(!showAddMenu)} style={{ ...btnPrimary, width: '100%', justifyContent: 'center' }}>
              <Plus size={16} /> Add Step
            </button>
            {showAddMenu && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '0.375rem', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 20, maxHeight: 360, overflowY: 'auto', marginTop: 4 }}>
                {Object.entries(groupByCategory()).map(([cat, ops]) => (
                  <div key={cat}>
                    <div style={{ padding: '0.5rem 0.75rem', fontSize: '0.7rem', fontWeight: 600, color: CATEGORY_COLORS[cat] || '#6b7280', backgroundColor: '#f9fafb', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{cat}</div>
                    {ops.map(op => (
                      <button key={op.value} onClick={() => addStep(op.value)} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.5rem 0.75rem', border: 'none', backgroundColor: '#fff', cursor: 'pointer', fontSize: '0.8rem' }}
                        onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f3f4f6')}
                        onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#fff')}
                      >
                        <span style={{ fontWeight: 500, color: '#111827' }}>{op.label}</span>
                        <span style={{ fontSize: '0.7rem', color: '#9ca3af', marginLeft: '0.5rem' }}>{op.description}</span>
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right: Output */}
        <div style={panelStyle}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
            <h2 style={panelTitle}>Output Preview</h2>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              {previewing && <span style={{ fontSize: '0.75rem', color: ACCENT }}>Running...</span>}
              <button onClick={copyOutput} disabled={outputData.length === 0} style={{ ...btnSmall, opacity: outputData.length === 0 ? 0.5 : 1 }}>
                {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy JSON</>}
              </button>
            </div>
          </div>

          {errors.length > 0 && (
            <div style={{ padding: '0.5rem 0.75rem', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '0.375rem', marginBottom: '0.75rem' }}>
              {errors.map((e, i) => (
                <p key={i} style={{ fontSize: '0.75rem', color: '#991b1b', margin: i === 0 ? 0 : '0.25rem 0 0' }}>{e}</p>
              ))}
            </div>
          )}

          {outputData.length === 0 && !previewing && (
            <div style={{ textAlign: 'center', padding: '2rem 1rem', color: '#9ca3af', fontSize: '0.85rem' }}>
              <Play size={32} style={{ margin: '0 auto 0.75rem', opacity: 0.3 }} />
              <p style={{ margin: 0 }}>Output appears here</p>
              <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem' }}>Add input data and transform steps</p>
            </div>
          )}

          {outputData.length > 0 && (
            <>
              <p style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                {outputData.length} rows, {getFieldNames(outputData).length} fields
              </p>
              <DataTable data={outputData} maxRows={20} />
            </>
          )}
        </div>
      </div>

      {/* Save Modal */}
      {showSaveModal && (
        <Modal onClose={() => setShowSaveModal(false)} title="Save Transform">
          <label style={labelStyle}>Name</label>
          <input value={saveName} onChange={e => setSaveName(e.target.value)} placeholder="e.g. Normalize Contacts" style={inputStyle} autoFocus />
          <label style={{ ...labelStyle, marginTop: '0.75rem' }}>Description</label>
          <textarea value={saveDesc} onChange={e => setSaveDesc(e.target.value)} placeholder="What does this transform do?" style={{ ...inputStyle, height: 80, resize: 'vertical' }} />
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', justifyContent: 'flex-end' }}>
            <button onClick={() => setShowSaveModal(false)} style={btnSecondary}>Cancel</button>
            <button onClick={handleSave} disabled={!saveName.trim() || saving} style={{ ...btnPrimary, opacity: !saveName.trim() || saving ? 0.5 : 1 }}>
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </Modal>
      )}

      {/* Load Modal */}
      {showLoadModal && (
        <Modal onClose={() => setShowLoadModal(false)} title="Load Transform">
          {savedTransforms.length === 0 ? (
            <p style={{ color: '#9ca3af', textAlign: 'center', padding: '2rem 0' }}>No saved transforms yet</p>
          ) : (
            <div style={{ maxHeight: 400, overflowY: 'auto' }}>
              {savedTransforms.map(t => (
                <div key={t.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem', borderBottom: '1px solid #f3f4f6' }}>
                  <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => loadTransform(t.id)}>
                    <p style={{ fontSize: '0.85rem', fontWeight: 500, color: '#111827', margin: 0 }}>{t.name}</p>
                    {t.description && <p style={{ fontSize: '0.75rem', color: '#9ca3af', margin: '0.125rem 0 0' }}>{t.description}</p>}
                    <p style={{ fontSize: '0.7rem', color: '#d1d5db', margin: '0.25rem 0 0' }}>{t.steps.length} steps | used {t.usage_count}x</p>
                  </div>
                  <button onClick={() => deleteSaved(t.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 4 }}><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}

// ── Step Config Components ──────────────────────────────────────────

function StepConfig({ step, fields, onChange }: { step: TransformStep; fields: string[]; onChange: (config: Record<string, unknown>) => void }) {
  const c = step.config;
  const set = (key: string, val: unknown) => onChange({ ...c, [key]: val });

  switch (step.operation) {
    case 'map_fields':
      return (
        <div style={{ marginTop: '0.5rem' }}>
          <p style={hintStyle}>Enter field mappings as old_name:new_name, one per line</p>
          <textarea
            value={Object.entries((c.mapping as Record<string, string>) || {}).map(([k, v]) => `${k}:${v}`).join('\n')}
            onChange={e => {
              const mapping: Record<string, string> = {};
              e.target.value.split('\n').forEach(line => {
                const [from, to] = line.split(':').map(s => s.trim());
                if (from && to) mapping[from] = to;
              });
              set('mapping', mapping);
            }}
            placeholder={'old_name:new_name\nemail:email_address'}
            style={{ ...inputStyle, height: 80, fontFamily: 'monospace', fontSize: '0.75rem' }}
          />
        </div>
      );

    case 'filter_rows':
      return (
        <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <FieldSelect label="Field" value={String(c.field || '')} fields={fields} onChange={v => set('field', v)} />
          <div>
            <label style={labelStyle}>Operator</label>
            <select value={String(c.operator || '=')} onChange={e => set('operator', e.target.value)} style={inputStyle}>
              {FILTER_OPERATORS.map(op => <option key={op} value={op}>{op}</option>)}
            </select>
          </div>
          {!['is_empty', 'is_not_empty'].includes(String(c.operator || '=')) && (
            <div>
              <label style={labelStyle}>Value</label>
              <input value={String(c.value ?? '')} onChange={e => set('value', e.target.value)} style={inputStyle} placeholder="Value" />
            </div>
          )}
        </div>
      );

    case 'sort':
      return (
        <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <FieldSelect label="Sort by" value={String(c.field || '')} fields={fields} onChange={v => set('field', v)} />
          <div>
            <label style={labelStyle}>Direction</label>
            <select value={String(c.direction || 'asc')} onChange={e => set('direction', e.target.value)} style={inputStyle}>
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </select>
          </div>
        </div>
      );

    case 'deduplicate':
      return (
        <div style={{ marginTop: '0.5rem' }}>
          <FieldSelect label="Key field" value={String(c.key || '')} fields={fields} onChange={v => set('key', v)} />
        </div>
      );

    case 'concat_fields':
      return (
        <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <div>
            <label style={labelStyle}>Fields (comma separated)</label>
            <input value={Array.isArray(c.fields) ? (c.fields as string[]).join(', ') : ''} onChange={e => set('fields', e.target.value.split(',').map(s => s.trim()).filter(Boolean))} style={inputStyle} placeholder="field1, field2" />
          </div>
          <div>
            <label style={labelStyle}>Separator</label>
            <input value={String(c.separator ?? ' ')} onChange={e => set('separator', e.target.value)} style={inputStyle} placeholder="Space, comma, etc." />
          </div>
          <div>
            <label style={labelStyle}>Output field</label>
            <input value={String(c.output_field ?? '')} onChange={e => set('output_field', e.target.value)} style={inputStyle} placeholder="combined_field" />
          </div>
        </div>
      );

    case 'split_field':
      return (
        <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <FieldSelect label="Field" value={String(c.field || '')} fields={fields} onChange={v => set('field', v)} />
          <div>
            <label style={labelStyle}>Separator</label>
            <input value={String(c.separator ?? ',')} onChange={e => set('separator', e.target.value)} style={inputStyle} placeholder="," />
          </div>
          <div>
            <label style={labelStyle}>Output fields (comma separated, optional)</label>
            <input value={Array.isArray(c.output_fields) ? (c.output_fields as string[]).join(', ') : ''} onChange={e => set('output_fields', e.target.value.split(',').map(s => s.trim()).filter(Boolean))} style={inputStyle} placeholder="first_name, last_name" />
          </div>
        </div>
      );

    case 'regex_extract':
      return (
        <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <FieldSelect label="Field" value={String(c.field || '')} fields={fields} onChange={v => set('field', v)} />
          <div>
            <label style={labelStyle}>Regex pattern</label>
            <input value={String(c.pattern ?? '')} onChange={e => set('pattern', e.target.value)} style={{ ...inputStyle, fontFamily: 'monospace' }} placeholder="(\d{3})-(\d{4})" />
          </div>
          <div>
            <label style={labelStyle}>Capture group (0=full match)</label>
            <input type="number" value={String(c.group_index ?? 0)} onChange={e => set('group_index', parseInt(e.target.value) || 0)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Output field</label>
            <input value={String(c.output_field ?? '')} onChange={e => set('output_field', e.target.value)} style={inputStyle} placeholder="extracted" />
          </div>
        </div>
      );

    case 'date_format':
      return (
        <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <FieldSelect label="Field" value={String(c.field || '')} fields={fields} onChange={v => set('field', v)} />
          <div>
            <label style={labelStyle}>Output format</label>
            <select value={String(c.output_format || 'iso')} onChange={e => set('output_format', e.target.value)} style={inputStyle}>
              {DATE_FORMATS.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
        </div>
      );

    case 'number_format':
      return (
        <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <FieldSelect label="Field" value={String(c.field || '')} fields={fields} onChange={v => set('field', v)} />
          <div>
            <label style={labelStyle}>Format</label>
            <select value={String(c.format || 'round')} onChange={e => set('format', e.target.value)} style={inputStyle}>
              {NUMBER_FORMATS.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Decimals</label>
            <input type="number" value={String(c.decimals ?? 2)} onChange={e => set('decimals', parseInt(e.target.value) || 0)} style={inputStyle} min={0} max={10} />
          </div>
        </div>
      );

    case 'lookup_replace':
      return (
        <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <FieldSelect label="Field" value={String(c.field || '')} fields={fields} onChange={v => set('field', v)} />
          <div>
            <p style={hintStyle}>Enter lookup pairs as old_value:new_value, one per line</p>
            <textarea
              value={Object.entries((c.lookup_table as Record<string, string>) || {}).map(([k, v]) => `${k}:${v}`).join('\n')}
              onChange={e => {
                const table: Record<string, string> = {};
                e.target.value.split('\n').forEach(line => {
                  const idx = line.indexOf(':');
                  if (idx > 0) table[line.substring(0, idx).trim()] = line.substring(idx + 1).trim();
                });
                set('lookup_table', table);
              }}
              placeholder={'US:United States\nUK:United Kingdom'}
              style={{ ...inputStyle, height: 80, fontFamily: 'monospace', fontSize: '0.75rem' }}
            />
          </div>
        </div>
      );

    case 'default_value':
      return (
        <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <FieldSelect label="Field" value={String(c.field || '')} fields={fields} onChange={v => set('field', v)} />
          <div>
            <label style={labelStyle}>Default value</label>
            <input value={String(c.value ?? '')} onChange={e => set('value', e.target.value)} style={inputStyle} placeholder="N/A" />
          </div>
        </div>
      );

    case 'uppercase':
    case 'lowercase':
    case 'trim':
      return (
        <div style={{ marginTop: '0.5rem' }}>
          <FieldSelect label="Field (leave empty for all)" value={String(c.field || '')} fields={fields} onChange={v => set('field', v)} allowEmpty />
        </div>
      );

    case 'json_parse':
      return (
        <div style={{ marginTop: '0.5rem' }}>
          <FieldSelect label="Field containing JSON string" value={String(c.field || '')} fields={fields} onChange={v => set('field', v)} />
        </div>
      );

    case 'json_path':
      return (
        <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <FieldSelect label="Field" value={String(c.field || '')} fields={fields} onChange={v => set('field', v)} />
          <div>
            <label style={labelStyle}>Path (dot notation)</label>
            <input value={String(c.path ?? '')} onChange={e => set('path', e.target.value)} style={{ ...inputStyle, fontFamily: 'monospace' }} placeholder="address.city" />
          </div>
          <div>
            <label style={labelStyle}>Output field</label>
            <input value={String(c.output_field ?? '')} onChange={e => set('output_field', e.target.value)} style={inputStyle} placeholder="city" />
          </div>
        </div>
      );

    case 'aggregate':
      return (
        <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <FieldSelect label="Field" value={String(c.field || '')} fields={fields} onChange={v => set('field', v)} />
          <div>
            <label style={labelStyle}>Operation</label>
            <select value={String(c.aggregate_op || 'sum')} onChange={e => set('aggregate_op', e.target.value)} style={inputStyle}>
              {AGGREGATE_OPS.map(op => <option key={op} value={op}>{op}</option>)}
            </select>
          </div>
        </div>
      );

    case 'group_by':
      return (
        <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <FieldSelect label="Group by field" value={String(c.group_field || '')} fields={fields} onChange={v => set('group_field', v)} />
          <FieldSelect label="Aggregate field (optional)" value={String(c.aggregate_field || '')} fields={fields} onChange={v => set('aggregate_field', v)} allowEmpty />
          <div>
            <label style={labelStyle}>Aggregate operation</label>
            <select value={String(c.aggregate_op || 'count')} onChange={e => set('aggregate_op', e.target.value)} style={inputStyle}>
              {AGGREGATE_OPS.map(op => <option key={op} value={op}>{op}</option>)}
            </select>
          </div>
        </div>
      );

    case 'calculate':
      return (
        <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <div>
            <label style={labelStyle}>Expression</label>
            <input value={String(c.expression ?? '')} onChange={e => set('expression', e.target.value)} style={{ ...inputStyle, fontFamily: 'monospace' }} placeholder="{price} * {quantity}" />
            <p style={hintStyle}>Use {'{field_name}'} to reference fields. Supports +, -, *, /</p>
          </div>
          <div>
            <label style={labelStyle}>Output field</label>
            <input value={String(c.output_field ?? '')} onChange={e => set('output_field', e.target.value)} style={inputStyle} placeholder="total" />
          </div>
        </div>
      );

    case 'merge':
      return (
        <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <FieldSelect label="Join on field" value={String(c.join_field || '')} fields={fields} onChange={v => set('join_field', v)} />
          <div>
            <label style={labelStyle}>Merge data (JSON array)</label>
            <textarea
              value={typeof c.merge_data === 'string' ? String(c.merge_data) : JSON.stringify(c.merge_data || [], null, 2)}
              onChange={e => {
                try { set('merge_data', JSON.parse(e.target.value)); } catch { set('merge_data', e.target.value); }
              }}
              placeholder={'[{"id":"1","extra":"value"}]'}
              style={{ ...inputStyle, height: 80, fontFamily: 'monospace', fontSize: '0.75rem' }}
            />
          </div>
        </div>
      );

    default:
      return <p style={{ ...hintStyle, marginTop: '0.5rem' }}>No configuration needed</p>;
  }
}

// ── Reusable Components ─────────────────────────────────────────────

function FieldSelect({ label, value, fields, onChange, allowEmpty }: { label: string; value: string; fields: string[]; onChange: (v: string) => void; allowEmpty?: boolean }) {
  return (
    <div>
      <label style={labelStyle}>{label}</label>
      <select value={value} onChange={e => onChange(e.target.value)} style={inputStyle}>
        {allowEmpty && <option value="">(all fields)</option>}
        {!allowEmpty && !value && <option value="">Select field...</option>}
        {fields.map(f => <option key={f} value={f}>{f}</option>)}
      </select>
    </div>
  );
}

function DataTable({ data, maxRows }: { data: Record<string, unknown>[]; maxRows: number }) {
  const rows = data.slice(0, maxRows);
  const cols = getFieldNames(data);
  if (cols.length === 0) return null;

  return (
    <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: '0.375rem' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
        <thead>
          <tr>
            {cols.map(col => (
              <th key={col} style={{ padding: '0.375rem 0.5rem', textAlign: 'left', fontWeight: 600, color: '#374151', backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' }}>
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {cols.map(col => (
                <td key={col} style={{ padding: '0.375rem 0.5rem', borderBottom: '1px solid #f3f4f6', color: '#4b5563', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {formatCellValue(row[col])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {data.length > maxRows && (
        <p style={{ textAlign: 'center', padding: '0.375rem', fontSize: '0.7rem', color: '#9ca3af', margin: 0, borderTop: '1px solid #e5e7eb' }}>
          Showing {maxRows} of {data.length} rows
        </p>
      )}
    </div>
  );
}

function formatCellValue(val: unknown): string {
  if (val === null || val === undefined) return '';
  if (typeof val === 'object') return JSON.stringify(val);
  return String(val);
}

function Modal({ onClose, title, children }: { onClose: () => void; title: string; children: React.ReactNode }) {
  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }} onClick={onClose}>
      <div style={{ backgroundColor: '#fff', borderRadius: '0.75rem', padding: '1.5rem', width: 480, maxWidth: '90vw', maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <h3 style={{ fontSize: '1.125rem', fontWeight: 600, margin: 0, color: '#111827' }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 4 }}><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function groupByCategory() {
  const groups: Record<string, typeof OPERATIONS> = {};
  for (const op of OPERATIONS) {
    if (!groups[op.category]) groups[op.category] = [];
    groups[op.category].push(op);
  }
  return groups;
}

// ── Styles ──────────────────────────────────────────────────────────

const panelStyle: React.CSSProperties = {
  backgroundColor: '#fff',
  borderRadius: '0.75rem',
  border: '1px solid #e5e7eb',
  padding: '1rem',
  minHeight: 400,
};

const panelTitle: React.CSSProperties = {
  fontSize: '0.9rem',
  fontWeight: 600,
  color: '#111827',
  margin: 0,
};

const btnPrimary: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.375rem',
  padding: '0.5rem 1rem',
  backgroundColor: ACCENT,
  color: '#fff',
  border: 'none',
  borderRadius: '0.375rem',
  fontSize: '0.85rem',
  fontWeight: 500,
  cursor: 'pointer',
};

const btnSecondary: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.375rem',
  padding: '0.5rem 1rem',
  backgroundColor: '#fff',
  color: '#374151',
  border: '1px solid #d1d5db',
  borderRadius: '0.375rem',
  fontSize: '0.85rem',
  fontWeight: 500,
  cursor: 'pointer',
};

const btnSmall: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.25rem',
  padding: '0.25rem 0.5rem',
  backgroundColor: '#f3f4f6',
  color: '#374151',
  border: '1px solid #e5e7eb',
  borderRadius: '0.25rem',
  fontSize: '0.75rem',
  cursor: 'pointer',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.375rem 0.5rem',
  border: '1px solid #d1d5db',
  borderRadius: '0.25rem',
  fontSize: '0.8rem',
  boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.75rem',
  fontWeight: 500,
  color: '#374151',
  marginBottom: '0.25rem',
};

const hintStyle: React.CSSProperties = {
  fontSize: '0.7rem',
  color: '#9ca3af',
  margin: '0.25rem 0',
};
